import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import bookdocLogo from '../../assets/images/bookdoc-logo.svg';
import CorpDashboardApi from '../../services/CorpDashboardApi';
import '../../assets/styles/CorporateDashboardLogin.scss';

const PAGE_TITLE = 'Corporate Dashboard - BookDoc';
const PAGE_DESCRIPTION = 'Sign in to the BookDoc corporate wellness dashboard with your HR administrator account.';

const EyeIcon = () => (
    <svg viewBox='0 0 24 24' fill='none' strokeWidth={1.5} stroke='currentColor' aria-hidden='true'>
        <path strokeLinecap='round' strokeLinejoin='round' d='M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z' />
        <path strokeLinecap='round' strokeLinejoin='round' d='M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z' />
    </svg>
);

const EyeSlashIcon = () => (
    <svg viewBox='0 0 24 24' fill='none' strokeWidth={1.5} stroke='currentColor' aria-hidden='true'>
        <path strokeLinecap='round' strokeLinejoin='round' d='M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88' />
    </svg>
);

function CorporateDashboardLogin() {
    const navigate = useNavigate();
    const location = useLocation();
    const from = (location.state && location.state.from) || '/corporate-dashboard';

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    if (CorpDashboardApi.isAuthenticated()) {
        return <Navigate to='/corporate-dashboard' replace />;
    }

    const incomplete = !email.trim() || !password.trim();

    const onSubmit = (event) => {
        event.preventDefault();
        if (incomplete || submitting) return;

        setError('');
        setSubmitting(true);

        CorpDashboardApi.login(email.trim(), password).then(
            () => navigate(from, { replace: true }),
            (err) => {
                setError(CorpDashboardApi.normaliseError(err));
                setSubmitting(false);
            }
        );
    };

    return (
        <div className='corp-login'>
            <Helmet>
                <title>{PAGE_TITLE}</title>
                <meta name='description' content={PAGE_DESCRIPTION} />
                <meta name='robots' content='noindex, nofollow' />
                <meta property='og:title' content={PAGE_TITLE} />
                <meta property='og:description' content={PAGE_DESCRIPTION} />
            </Helmet>

            <div className='corp-login__card'>
                <img className='corp-login__logo' src={bookdocLogo} alt='BookDoc' />

                <h1 className='corp-login__title'>Corporate Dashboard</h1>
                <p className='corp-login__subtitle'>Sign in with your HR administrator account.</p>

                <form onSubmit={onSubmit} noValidate>
                    {error && <p className='corp-login__error' role='alert'>{error}</p>}

                    <div className='corp-login__field'>
                        <input
                            id='email'
                            className='corp-login__input'
                            type='email'
                            name='email'
                            placeholder='Email'
                            autoComplete='email'
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>

                    <div className='corp-login__field corp-login__field--password'>
                        <input
                            id='password'
                            className='corp-login__input corp-login__input--password'
                            type={showPassword ? 'text' : 'password'}
                            name='password'
                            placeholder='Password'
                            autoComplete='current-password'
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <button
                            type='button'
                            className='corp-login__toggle'
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            onClick={() => setShowPassword((visible) => !visible)}
                        >
                            {showPassword ? <EyeIcon /> : <EyeSlashIcon />}
                        </button>
                    </div>

                    <button type='submit' className='corp-login__submit' disabled={incomplete || submitting}>
                        {submitting ? 'Signing In…' : 'Sign In'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default CorporateDashboardLogin;
